import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFormPopupContext } from '../../contexts/FormPopupContext';
import { products, testimonials, awards } from '../../utils/data';
import HeroSection from '../../components/HeroSection';

const Pro8KProjector: React.FC = () => {
  const product = products.find(p => p.id === 'pro-8k-projector');
  const productTestimonials = testimonials.filter(t => t.products?.includes('pro-8k-projector'));
  const productAwards = awards.filter(a => a.title.includes('Innovation') || a.title.includes('Editor'));
  const { openFormPopup } = useFormPopupContext();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="min-h-screen">
      {/* CINEMATIC HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-4.png"
        mobileImage="/images/mobile-hero-4.png"
        title={
          <>
            <span className="text-gradient block">Parallax Pro 8K</span>
            <span className="block text-3xl md:text-4xl text-secondary mt-2 font-normal">
              Redefining Visual Perfection
            </span>
          </>
        }
        subtitle="The Pro 8K transcends conventional projection technology. With native 8K resolution and our proprietary lens system, every frame becomes a window into the director's vision."
        tagline={`Investment: ${product.priceRange}`}
      >
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          <button 
            onClick={openFormPopup}
            className="btn-primary text-base sm:text-lg whitespace-nowrap"
          >
            Experience The Vision
          </button>
          <button 
            onClick={openFormPopup}
            className="btn-secondary text-base sm:text-lg whitespace-nowrap"
          >
            Schedule Private Viewing
          </button>
        </motion.div>
      </HeroSection>

      {/* PHILOSOPHY & VISION SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                  Where Engineering Meets Artistry
                </h2>
                <div className="space-y-6 text-lg text-secondary/90 leading-relaxed">
                  <p>
                    The Parallax Pro 8K represents the culmination of three years of obsessive 
                    engineering and artistic vision. Each component has been meticulously crafted 
                    to not merely display content, but to transform your space into a sanctuary 
                    of cinematic excellence.
                  </p>
                  <p>
                    Experience color accuracy that rivals the world's finest studios, brightness 
                    that illuminates even well-lit spaces, and contrast ratios that reveal details 
                    invisible to lesser projectors.
                  </p>
                </div>
              </motion.div>
              
              {/* Visual element - lens system artistic render */}
              <motion.div 
                className="relative"
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="relative rounded-xl overflow-hidden border border-highlight/20 bg-primary/40 shadow-2xl">
                  <div className="relative aspect-[2/1]">
                    <img
                      src="/images/parallax-pro-8k-projector-branded.jpg"
                      alt="Parallax Pro 8K Projector"
                      className="w-full h-full object-cover"
                      loading="eager"
                      decoding="async"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ENGINEERING ARTISTRY (Technical Specs as Stories) */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                Precision-Engineered Excellence
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Every specification tells a story of uncompromising pursuit of perfection
              </p>
            </motion.div>
            
            {/* Technical specs as storytelling cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Native 8K Resolution */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">7680×4320</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Native 8K Resolution</h3>
                <p className="text-secondary/80">
                  Every pixel rendered with surgical precision. Experience detail so fine 
                  you'll rediscover films you thought you knew.
                </p>
              </motion.div>
              
              {/* Laser Light Source */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">4,000+ Lumens</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Laser Phosphor Engine</h3>
                <p className="text-secondary/80">
                  Illumination that brings life to every scene. Our proprietary laser system 
                  delivers consistent, true-to-life brightness for decades.
                </p>
              </motion.div>

              {/* Color Accuracy */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">DCI-P3 100%</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Cinema-Grade Color</h3>
                <p className="text-secondary/80">
                  Color accuracy that rivals professional cinema theaters. Every hue rendered 
                  exactly as the filmmaker intended.
                </p>
              </motion.div>

              {/* Contrast Ratio */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">1,000,000:1</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Dynamic Contrast</h3>
                <p className="text-secondary/80">
                  Infinite depths of black and brilliant highlights reveal details 
                  hidden in shadows and bright scenes alike.
                </p>
              </motion.div>

              {/* Precision Optics */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">Motorized</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Precision Lens System</h3>
                <p className="text-secondary/80">
                  Advanced motorized zoom, focus, and lens shift. Perfect image 
                  placement with micrometer-level precision.
                </p>
              </motion.div>

              {/* Silent Operation */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-3xl font-light mb-4">Silent</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Whisper-Quiet Operation</h3>
                <p className="text-secondary/80">
                  Advanced thermal management ensures virtually silent operation, 
                  preserving the magic of cinematic immersion.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERACTIVE SPECIFICATIONS TABLE */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              className="text-3xl md:text-4xl font-light text-center mb-12 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Technical Mastery
            </motion.h2>
            
            {/* Elegant specs table */}
            <motion.div 
              className="projector-glass-effect rounded-xl p-8 md:p-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                {Object.entries(product.specifications).map(([key, value], index) => (
                  <motion.div 
                    key={key} 
                    className="flex justify-between items-center border-b border-secondary/10 pb-4 last:border-b-0"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <span className="text-secondary/80 font-medium text-lg">{key}</span>
                    <span className="text-white font-light text-lg">{value}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EXPERIENCE & INSTALLATION SECTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                The Parallax Experience
              </h2>
              <p className="text-xl text-secondary/80 mb-16 max-w-3xl mx-auto">
                From consultation to calibration, every touchpoint is designed to exceed 
                the expectations of the world's most discerning clients.
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-12">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-10 h-10 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">Private Consultation</h3>
                <p className="text-secondary/80 leading-relaxed">
                  Our specialists visit your space to design the perfect cinematic experience 
                  tailored to your environment and preferences.
                </p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-10 h-10 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">White-Glove Installation</h3>
                <p className="text-secondary/80 leading-relaxed">
                  Precision installation and calibration by certified artisans, 
                  ensuring optimal performance in your unique environment.
                </p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-6 flex items-center justify-center">
                  <svg className="w-10 h-10 text-highlight" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4">Lifetime Concierge</h3>
                <p className="text-secondary/80 leading-relaxed">
                  Ongoing support and optimization for your cinematic sanctuary, 
                  ensuring your investment performs flawlessly for years to come.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* AWARDS & TESTIMONIALS */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-light mb-6 text-gradient">
                Recognized Excellence
              </h2>
              <p className="text-xl text-secondary/80">
                Industry acclaim for uncompromising innovation
              </p>
            </motion.div>

            {/* Awards */}
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              {productAwards.map((award, index) => (
                <motion.div 
                  key={index}
                  className="glass-effect p-8 rounded-xl text-center"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-highlight/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-highlight" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-accent mb-2">{award.title}</h3>
                  <p className="text-secondary/80 mb-2">{award.organization}</p>
                  <p className="text-xs text-highlight">{award.year}</p>
                </motion.div>
              ))}
            </div>

            {/* Testimonials */}
            {productTestimonials.length > 0 && (
              <div className="grid gap-8">
                {productTestimonials.map((testimonial, index) => (
                  <motion.div 
                    key={testimonial.id}
                    className="glass-effect p-8 rounded-xl"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex text-highlight mb-4">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="text-lg text-secondary/90 leading-relaxed mb-6 italic">
                      "{testimonial.content}"
                    </p>
                    <div>
                      <p className="font-semibold text-accent text-lg">{testimonial.name}</p>
                      <p className="text-secondary/80">{testimonial.role}</p>
                      {testimonial.company && (
                        <p className="text-highlight text-sm">{testimonial.company}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                Begin Your Cinematic Journey
              </h2>
              <p className="text-xl text-secondary/80 mb-12 leading-relaxed">
                Experience the Parallax Pro 8K in our private showroom or schedule 
                an in-home consultation with our specialists. Transform your space into 
                a sanctuary of cinematic excellence.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center max-w-2xl mx-auto">
                <button 
                  onClick={openFormPopup}
                  className="btn-primary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Schedule Private Viewing
                </button>
                <button 
                  onClick={openFormPopup}
                  className="btn-secondary text-lg px-12 py-4 flex-1 sm:flex-none whitespace-nowrap"
                >
                  Request Consultation
                </button>
              </div>

              <div className="mt-12 text-center">
                <p className="text-secondary/60 text-sm mb-4">
                  Pricing includes professional installation and calibration
                </p>
                <div className="inline-flex items-center gap-4 text-xs text-secondary/40 uppercase tracking-wider">
                  <span>White-Glove Service</span>
                  <span>•</span>
                  <span>Lifetime Support</span>
                  <span>•</span>
                  <span>Expert Consultation</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popup handled globally */}
    </div>
  );
};

export default Pro8KProjector; 
